import { jsPDF } from 'jspdf';

/**
 * exportFprFormToPdf — render the FPR Update Form data to a downloadable
 * PDF using jsPDF text/rect primitives (no DOM capture). Matches the
 * sectioning logic of FprFormView but produces a clean, paginated PDF.
 *
 * Rules:
 *   - Every field rendered, em-dash for blanks.
 *   - Sub-fields skipped if their parent gate (yes/no or life-event
 *     checkbox) is not affirmative.
 */

const EM_DASH = '\u2014';

const fmtCurrency = (v) => {
  if (v == null || v === '') return EM_DASH;
  const n = Number(v);
  if (!Number.isFinite(n)) return EM_DASH;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtText = (v) => (v == null || v === '' ? EM_DASH : String(v));

const fmtYesNo = (v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : EM_DASH);

const fmtBool = (v) => (v ? 'Yes' : 'No');

const titleize = (s) =>
  String(s || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const hasAnyData = (obj) =>
  obj && Object.values(obj).some((v) => v !== null && v !== undefined && v !== '');

/* ── PDF layout helpers ──────────────────────────────────────────── */

const PAGE = { width: 612, height: 792 }; // letter @ 72dpi
const MARGIN = { top: 48, right: 48, bottom: 48, left: 48 };
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;

const COLORS = {
  text: [33, 37, 41],
  muted: [108, 117, 125],
  accent: [90, 31, 31],
  accentSoft: [248, 249, 250],
  rule: [222, 226, 230],
  noneBg: [231, 245, 236],
  noneRule: [25, 135, 84],
  notesBg: [255, 251, 234],
  notesRule: [255, 193, 7],
  totalBg: [240, 247, 255],
  totalRule: [207, 226, 255],
  subBg: [248, 249, 250],
  subRule: [233, 236, 239],
};

class PdfWriter {
  constructor(doc) {
    this.doc = doc;
    this.y = MARGIN.top;
  }

  needSpace(h) {
    if (this.y + h > PAGE.height - MARGIN.bottom) {
      this.doc.addPage();
      this.y = MARGIN.top;
    }
  }

  setFill(rgb) {
    this.doc.setFillColor(rgb[0], rgb[1], rgb[2]);
  }
  setText(rgb) {
    this.doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  }
  setDraw(rgb) {
    this.doc.setDrawColor(rgb[0], rgb[1], rgb[2]);
  }

  documentHeader({ clientName, clientEmail }) {
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(16);
    this.setText(COLORS.text);
    this.doc.text('FPR Update Form', MARGIN.left, this.y + 16);
    this.y += 22;

    if (clientName || clientEmail) {
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(10);
      this.setText(COLORS.muted);
      const subtitle = [clientName, clientEmail].filter(Boolean).join(' — ');
      this.doc.text(subtitle, MARGIN.left, this.y);
      this.y += 12;
    }

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.setText(COLORS.muted);
    this.doc.text(
      `Generated ${new Date().toLocaleString()}`,
      MARGIN.left,
      this.y,
    );
    this.y += 6;

    this.setDraw(COLORS.accent);
    this.doc.setLineWidth(1.2);
    this.doc.line(MARGIN.left, this.y + 4, MARGIN.left + CONTENT_WIDTH, this.y + 4);
    this.y += 16;
  }

  sectionHeader(number, title) {
    const headerH = 22;
    this.needSpace(headerH + 30);
    this.setFill(COLORS.accentSoft);
    this.setDraw(COLORS.rule);
    this.doc.setLineWidth(0.4);
    this.doc.roundedRect(MARGIN.left, this.y, CONTENT_WIDTH, headerH, 3, 3, 'FD');

    // Number badge
    this.setFill(COLORS.accent);
    this.doc.circle(MARGIN.left + 14, this.y + headerH / 2, 7, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.setText([255, 255, 255]);
    this.doc.text(String(number), MARGIN.left + 14, this.y + headerH / 2 + 3, {
      align: 'center',
    });

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(11);
    this.setText(COLORS.text);
    this.doc.text(title, MARGIN.left + 28, this.y + headerH / 2 + 3.5);
    this.y += headerH + 6;
  }

  subheading(text) {
    this.needSpace(16);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(8);
    this.setText(COLORS.accent);
    this.doc.text(text.toUpperCase(), MARGIN.left + 4, this.y + 8);
    this.y += 11;
    this.setDraw(COLORS.rule);
    this.doc.setLineWidth(0.3);
    this.doc.line(MARGIN.left + 4, this.y, MARGIN.left + CONTENT_WIDTH - 4, this.y);
    this.y += 3;
  }

  row(label, value, indent = 0) {
    const labelX = MARGIN.left + 4 + indent;
    const valueX = MARGIN.left + 4 + indent + 180;
    const valueWidth = CONTENT_WIDTH - 8 - 180 - indent;

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);

    const valueStr = value == null ? EM_DASH : String(value);
    const valueLines = this.doc.splitTextToSize(valueStr, valueWidth);
    const labelLines = this.doc.splitTextToSize(String(label), 175);
    const rowHeight = Math.max(labelLines.length, valueLines.length) * 11 + 2;

    this.needSpace(rowHeight + 4);

    this.setText(COLORS.muted);
    this.doc.text(labelLines, labelX, this.y + 8);
    this.setText(COLORS.text);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(valueLines, valueX, this.y + 8);
    this.y += rowHeight;
  }

  noneBanner(text) {
    this.needSpace(18);
    this.setFill(COLORS.noneBg);
    this.doc.rect(MARGIN.left, this.y, CONTENT_WIDTH, 16, 'F');
    this.setFill(COLORS.noneRule);
    this.doc.rect(MARGIN.left, this.y, 3, 16, 'F');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.setText(COLORS.text);
    this.doc.text(text, MARGIN.left + 8, this.y + 11);
    this.y += 20;
  }

  eventLine(checked, label, indent = 0) {
    this.needSpace(14);
    const x = MARGIN.left + 4 + indent;
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(10);
    this.setText(COLORS.text);
    const box = checked ? '[x]' : '[ ]';
    this.doc.text(`${box}  ${label}`, x, this.y + 9);
    this.y += 13;
  }

  subblock(title, fn) {
    const startY = this.y;
    this.needSpace(20);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(9);
    this.setText(COLORS.accent);
    this.doc.text(title, MARGIN.left + 12, this.y + 11);
    this.y += 14;

    fn(this);

    // Draw subtle background after we know height
    const endY = this.y;
    if (endY > startY) {
      // Draw a left rule to indicate a sub-block (no fill to keep it light).
      this.setDraw(COLORS.subRule);
      this.doc.setLineWidth(0.6);
      this.doc.line(MARGIN.left + 8, startY + 2, MARGIN.left + 8, endY - 2);
      this.y += 2;
    }
  }

  notes(text) {
    if (!text) return;
    this.needSpace(20);
    const lines = this.doc.splitTextToSize(text, CONTENT_WIDTH - 16);
    const h = lines.length * 11 + 16;
    this.needSpace(h);

    this.setFill(COLORS.notesBg);
    this.doc.rect(MARGIN.left, this.y, CONTENT_WIDTH, h, 'F');
    this.setFill(COLORS.notesRule);
    this.doc.rect(MARGIN.left, this.y, 3, h, 'F');

    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(7.5);
    this.setText([133, 100, 4]);
    this.doc.text('NOTES / COMMENTS', MARGIN.left + 8, this.y + 9);

    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);
    this.setText(COLORS.text);
    this.doc.text(lines, MARGIN.left + 8, this.y + 20);
    this.y += h + 4;
  }

  total(label, valueText) {
    this.needSpace(20);
    this.setFill(COLORS.totalBg);
    this.setDraw(COLORS.totalRule);
    this.doc.setLineWidth(0.5);
    this.doc.roundedRect(MARGIN.left, this.y, CONTENT_WIDTH, 18, 2, 2, 'FD');
    this.doc.setFont('helvetica', 'bold');
    this.doc.setFontSize(10);
    this.setText(COLORS.text);
    this.doc.text(label, MARGIN.left + 8, this.y + 12);
    this.doc.text(valueText, MARGIN.left + CONTENT_WIDTH - 8, this.y + 12, {
      align: 'right',
    });
    this.y += 22;
  }

  empty(text) {
    this.needSpace(14);
    this.doc.setFont('helvetica', 'italic');
    this.doc.setFontSize(9);
    this.setText(COLORS.muted);
    this.doc.text(text, MARGIN.left + 4, this.y + 9);
    this.y += 13;
  }

  spacer(h = 6) {
    this.y += h;
  }

  pageFooter() {
    const totalPages = this.doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setFontSize(8);
      this.setText(COLORS.muted);
      this.doc.text(
        `Page ${i} of ${totalPages}`,
        PAGE.width - MARGIN.right,
        PAGE.height - 24,
        { align: 'right' },
      );
    }
  }
}

/* ── Section writers ─────────────────────────────────────────────── */

function writePersonal(w, d = {}) {
  w.subheading('Contact information');
  w.row('First name', fmtText(d.first_name));
  w.row('Last name', fmtText(d.last_name));
  w.row('Phone', fmtText(d.phone));
  w.row('Email', fmtText(d.email));
  w.row('Reviewed contact info', fmtBool(d.contact_verified));

  w.subheading('Household');
  w.row('Shares finances with partner', fmtYesNo(d.has_partner));
  if (d.has_partner === 'yes') {
    w.row('Partner first name', fmtText(d.partner_first_name));
    w.row('Partner last name', fmtText(d.partner_last_name));
    w.row('Partner phone', fmtText(d.partner_phone));
    w.row('Partner email', fmtText(d.partner_email));
  }

  w.subheading('Tools & Preparation');
  w.row('Uses eMoney', fmtYesNo(d.uses_emoney));
  w.row(
    'Investment-check frequency',
    fmtText(d.investment_check_frequency && titleize(d.investment_check_frequency)),
  );
  const sentiment = { green: 'Good', yellow: 'Mixed', red: 'Concerned' }[d.investment_sentiment]
    || EM_DASH;
  w.row('Feeling about investments', sentiment);
  w.row('Concerns / questions for advisor', fmtText(d.advisor_questions));
  w.row('Upcoming large expenses', fmtText(d.upcoming_large_expenses));
  w.notes(d.notes);
}

function writeFamilyPersonal(w, d = {}) {
  if (d.none_of_the_above) {
    w.noneBanner('No changes since last review.');
  }

  const events = [
    {
      key: 'married',
      label: 'Married',
      children: () => {
        w.row('Partner name', fmtText(d.married_partner_name), 16);
        w.row('Partner DOB', fmtText(d.married_partner_dob), 16);
        w.row('Updated beneficiaries / estate docs', fmtYesNo(d.married_updated_beneficiaries), 16);
      },
    },
    {
      key: 'divorced',
      label: 'Divorced / separated',
      children: () => {
        w.row('Updated beneficiaries / estate docs', fmtYesNo(d.divorced_updated_beneficiaries), 16);
      },
    },
    {
      key: 'widowed',
      label: 'Widowed',
      children: () => {
        w.row('Updated beneficiaries / estate docs', fmtYesNo(d.widowed_updated_beneficiaries), 16);
      },
    },
    {
      key: 'new_child',
      label: 'Had a baby / adopted a child',
      children: () => {
        const kids = (d.new_children || []).filter((c) => c && (c.name || c.dob));
        if (kids.length === 0) {
          w.row('Children', EM_DASH, 16);
        }
        kids.forEach((c, i) => {
          w.row(`Child #${i + 1}`, `${fmtText(c.name)} — DOB ${fmtText(c.dob)}`, 16);
        });
        w.row('Updated beneficiaries / estate docs', fmtYesNo(d.new_child_updated_beneficiaries), 16);
      },
    },
    {
      key: 'supporting_family',
      label: 'Began supporting / caring for family member',
      children: () => {
        w.row('Estimated annual expense', fmtCurrency(d.supporting_family_annual_expense), 16);
        w.row('Potential duration', fmtText(d.supporting_family_duration), 16);
        w.row('Name & relationship', fmtText(d.supporting_family_relation), 16);
      },
    },
    {
      key: 'moved',
      label: 'Moved / planning to relocate',
      children: () => {
        w.row('New address', fmtText(d.new_address), 16);
      },
    },
  ];

  events.forEach((evt) => {
    w.eventLine(!!d[evt.key], evt.label);
    if (d[evt.key]) {
      evt.children();
      w.spacer(2);
    }
  });
  w.notes(d.notes);
}

function writeCareerIncome(w, d = {}) {
  if (d.none_of_the_above) {
    w.noneBanner('No changes since last review.');
  }

  const events = [
    {
      key: 'changed_jobs',
      label: 'Changed jobs / promotion',
      children: () => {
        const jobs = (d.job_changes || []).filter(
          (j) => j && (j.person || j.title || j.employer || j.employer_address || j.annual_salary || j.annual_bonus),
        );
        if (jobs.length === 0) {
          w.row('Job changes', EM_DASH, 16);
          return;
        }
        jobs.forEach((j, i) => {
          w.subblock(`Job change #${i + 1}`, () => {
            w.row('Who', fmtText(j.person), 16);
            w.row('New job title', fmtText(j.title), 16);
            w.row('Employer', fmtText(j.employer), 16);
            w.row('Employer address', fmtText(j.employer_address), 16);
            w.row('Estimated annual salary', fmtCurrency(j.annual_salary), 16);
            w.row('Additional annual bonus', fmtText(j.annual_bonus), 16);
          });
        });
      },
    },
    {
      key: 'promoted',
      label: 'Change in compensation (bonus, equity, stock options)',
      children: () => {
        const ps = (d.promotions || []).filter((p) => p && (p.person || p.details));
        if (ps.length === 0) {
          w.row('Compensation changes', EM_DASH, 16);
          return;
        }
        ps.forEach((p, i) => {
          w.subblock(`Compensation change #${i + 1}`, () => {
            w.row('Who', fmtText(p.person), 16);
            w.row('Details', fmtText(p.details), 16);
          });
        });
      },
    },
    {
      key: 'started_or_sold_business',
      label: 'Started or sold a business',
      children: () => {
        w.row('Details', fmtText(d.started_or_sold_business_details), 16);
      },
    },
    {
      key: 'retired',
      label: 'Retired or planning to retire soon',
      children: () => {
        w.row('Details', fmtText(d.retired_details), 16);
      },
    },
  ];

  events.forEach((evt) => {
    w.eventLine(!!d[evt.key], evt.label);
    if (d[evt.key]) {
      evt.children();
      w.spacer(2);
    }
  });
  w.notes(d.notes);
}

function writeFinancialPlanning(w, d = {}) {
  if (d.none_of_the_above) {
    w.noneBanner('No changes since last review.');
  }

  const events = [
    { key: 'new_or_consolidated_accounts', label: 'Added, consolidated, or transferred accounts', detail: 'new_accounts_details' },
    { key: 'new_loan', label: 'Took out a new loan (mortgage, auto, HELOC, etc.)', detail: 'new_loan_details' },
    { key: 'changed_investment_approach', label: 'Changed investment approach or preferences', detail: 'changed_investment_approach_details' },
    { key: 'interested_in_strategies', label: 'Interested in strategies (Roth conversions, charitable giving, etc.)', detail: 'interested_in_strategies_details' },
    { key: 'large_purchase_or_sale', label: 'Made a large purchase or sale (home, second home, major asset)', detail: 'large_purchase_or_sale_details' },
    { key: 'insurance_changes', label: 'Purchased or updated insurance', detail: 'insurance_changes_details' },
    { key: 'estate_changes', label: 'Updated estate documents or beneficiaries', detail: 'estate_changes_details' },
    { key: 'inheritance_or_gift', label: 'Received an inheritance or large gift', detail: 'inheritance_or_gift_details' },
  ];

  events.forEach((evt) => {
    w.eventLine(!!d[evt.key], evt.label);
    if (d[evt.key]) {
      w.row('Details', fmtText(d[evt.detail]), 16);
      w.spacer(2);
    }
  });
  w.notes(d.notes);
}

function writeAccounts(w, d = {}) {
  const entries = (d.entries || []).filter(hasAnyData);
  if (entries.length === 0) {
    w.empty('No accounts listed.');
  }
  entries.forEach((e, i) => {
    w.subblock(`Account #${i + 1}`, () => {
      w.row('Type', fmtText(e.type), 16);
      w.row('Institution', fmtText(e.institution), 16);
      w.row('Current / average value', fmtCurrency(e.value), 16);
      w.row(
        'Interest rate (%)',
        e.interest_rate == null || e.interest_rate === '' ? EM_DASH : `${e.interest_rate}%`,
        16,
      );
      if (e.type === 'CD') {
        w.row('Maturity date', fmtText(e.maturity_date), 16);
      }
    });
  });
  w.notes(d.notes);
}

function writeIncome(w, d = {}) {
  const sources = (d.sources || []).filter(
    (s) => s && (s.type || s.description || s.amount),
  );
  if (sources.length === 0) {
    w.empty('No income sources listed.');
  }
  sources.forEach((s, i) => {
    w.subblock(`Source #${i + 1}`, () => {
      w.row('Type', fmtText(s.type), 16);
      w.row('Description', fmtText(s.description), 16);
      w.row('Amount (annualised)', fmtCurrency(s.amount), 16);
    });
  });
  const total = sources.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  if (total > 0) {
    w.total('Total annualised income', `$${Math.round(total).toLocaleString()}`);
  }
  w.notes(d.notes);
}

function writeDebts(w, d = {}) {
  const entries = (d.entries || []).filter(hasAnyData);
  if (entries.length === 0) {
    w.empty('No debts listed.');
  }
  entries.forEach((e, i) => {
    w.subblock(`Debt #${i + 1}`, () => {
      w.row('Type', fmtText(e.type), 16);
      w.row('Institution', fmtText(e.institution), 16);
      w.row('Principal balance', fmtCurrency(e.principal_balance), 16);
      w.row('Monthly payment', fmtCurrency(e.monthly_payment), 16);
      w.row(
        'Interest rate (%)',
        e.interest_rate == null || e.interest_rate === '' ? EM_DASH : `${e.interest_rate}%`,
        16,
      );
      w.row('Remaining term', fmtText(e.remaining_term), 16);
      w.row('New since last met?', fmtText(e.is_new && titleize(e.is_new)), 16);
    });
  });
  w.notes(d.notes);
}

function writeExpenses(w, d = {}) {
  const expenses = (d.expenses || []).filter(
    (e) => e && (e.category || e.custom_label || e.amount),
  );
  if (expenses.length === 0) {
    w.empty('No expenses listed.');
  }
  expenses.forEach((e, i) => {
    const label =
      e.category === 'Other' && e.custom_label
        ? `Other — ${e.custom_label}`
        : (e.category || EM_DASH);
    w.subblock(`Expense #${i + 1}`, () => {
      w.row('Category', label, 16);
      w.row('Amount (annualised)', fmtCurrency(e.amount), 16);
    });
  });
  w.notes(d.notes);
}

function writeDocuments(w, d = {}) {
  const files = Array.isArray(d.uploaded_files) ? d.uploaded_files : [];
  if (files.length === 0) {
    w.empty('No documents uploaded.');
  } else {
    files.forEach((f, i) => {
      w.row(`File #${i + 1}`, fmtText(f.name || f.filename || f));
    });
  }
  w.notes(d.notes);
}

function writeSchedule(w, d = {}) {
  const booked = !!(
    d.booking_detected
    || d.booking_data?.bookingInfo?.statusCode === '1'
    || (d.booking_data?.bookingInfo?.MeetingTimes || []).length > 0
    || String(d.booking_data?.bookedstatus || '').toLowerCase() === 'scheduled'
  );
  w.row('Meeting status', booked ? 'Scheduled' : 'Not yet scheduled');
  if (d.booking_data?.bookingInfo?.MeetingTimes?.[0]) {
    w.row('Start time', fmtText(d.booking_data.bookingInfo.MeetingTimes[0].StartTime));
  }
}

const SECTIONS = [
  { number: 1, key: 'personal', title: 'Personal Information', write: writePersonal },
  { number: 2, key: 'family_personal', title: 'Situation Updates — Family & Personal', write: writeFamilyPersonal },
  { number: 3, key: 'career_income', title: 'Situation Updates — Career & Income', write: writeCareerIncome },
  { number: 4, key: 'financial_planning', title: 'Situation Updates — Financial & Planning', write: writeFinancialPlanning },
  { number: 5, key: 'accounts', title: 'Cash & Savings Accounts', write: writeAccounts },
  { number: 6, key: 'income', title: 'Income & Recurring Sources', write: writeIncome },
  { number: 7, key: 'debts', title: 'Debts & Loans', write: writeDebts },
  { number: 8, key: 'expenses', title: 'Expenses', write: writeExpenses },
  { number: 9, key: 'documents', title: 'Documents', write: writeDocuments },
  { number: 10, key: 'schedule_meeting', title: 'Schedule Meeting', write: writeSchedule },
];

/** Build the jsPDF document (in-memory). */
function buildFprPdfDoc(formData, meta = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const w = new PdfWriter(doc);
  w.documentHeader({ clientName: meta.clientName, clientEmail: meta.clientEmail });

  SECTIONS.forEach((s) => {
    w.sectionHeader(s.number, s.title);
    s.write(w, formData?.[s.key] || {});
    w.spacer(8);
  });

  w.pageFooter();
  return doc;
}

/** Derive a "Last Name" suitable for default filenames. */
function deriveLastName(clientName) {
  if (!clientName) return '';
  const trimmed = String(clientName).trim();
  if (trimmed.includes(',')) return trimmed.split(',')[0].trim();
  const parts = trimmed.split(/\s+/);
  return parts[parts.length - 1] || '';
}

/** Default file name in the form: FPR-Update Form-[Last]-[Year].pdf */
export function defaultFprPdfFilename(clientName) {
  const last = deriveLastName(clientName) || 'Client';
  const year = new Date().getFullYear();
  return `FPR-Update Form-${last}-${year}.pdf`;
}

/**
 * Build and download the PDF.
 *
 * @param {object} formData
 * @param {object} meta - { clientName, clientEmail, fileName }
 */
export function exportFprFormToPdf(formData, meta = {}) {
  const doc = buildFprPdfDoc(formData, meta);
  doc.save(meta.fileName || defaultFprPdfFilename(meta.clientName));
}

/**
 * Build the PDF and return it as a Blob for upload (e.g. SharePoint).
 *
 * @param {object} formData
 * @param {object} meta - { clientName, clientEmail }
 * @returns {Blob}
 */
export function buildFprPdfBlob(formData, meta = {}) {
  const doc = buildFprPdfDoc(formData, meta);
  return doc.output('blob');
}

export default exportFprFormToPdf;
